'use client';

import { useState } from 'react';
import ShoppingCartItemRateDetails from './shopping-cart-item-rate-details';
import { Information, IconCollapse } from '../../icon';
import { useMediaQuery } from 'react-responsive';
import { useTranslations } from '@fshr/context/i18n-context';
import Button from '../../button';
import { cn } from '@fshr/shared-react';
import { handlePriceFormat } from './price-functions';
import { StayResortFeesType } from '../../types/shopping-cart-item';
import { RoomRateType } from '../../types/shopping-cart-item';
import CloseCircle from '../../icon/close_circle';
import CheckmarkIcon from '../../icon/checkmark-icon';
import { DialogOfferDataType, RoomProps } from '../../types';
import RateCardDialog from '../../rate-card';
import FloorPlanComponent from '../../room-and-floorplan-modal';
import BevSafeText from '../../bev-safe-text';

type ShoppingCartItemProps = {
	currencyCode: string;
	name: string;
	cartDescription?: string;
	roomRate?: RoomRateType;
	numberOfPeople?: string;
	duration?: string;
	price?: number;
	stayResortFees?: StayResortFeesType[];
	taxAndFees?: string;
	quantity?: number;
	productType?: string;
	unitType?: string;
	complimentaryPricingInfo?: string;
	requestDate?: string | null;
	itemTitle?: string;
	checkInDate?: string;
	checkOutDate?: string;
	removeItem?: () => void;
	showInfoModal?: () => void;
	openOfferDialog?: (args: { offerData: DialogOfferDataType }) => void;
	ppMode?: boolean;
	ppRate?: boolean;
	isMultiRoom?: boolean;
	isCartEnabled?: boolean;
	room?: RoomProps;
	offer?: DialogOfferDataType;
};

export function ShoppingCartItem({
	currencyCode,
	roomRate,
	name,
	cartDescription,
	numberOfPeople,
	duration,
	price,
	stayResortFees,
	taxAndFees = 'Taxes & fees may apply',
	quantity,
	unitType,
	complimentaryPricingInfo,
	requestDate,
	itemTitle,
	removeItem,
	showInfoModal,
	openOfferDialog,
	ppMode,
	ppRate,
	isMultiRoom = false,
	room,
	offer,
}: ShoppingCartItemProps) {
	const t = useTranslations();
	const isMobile = useMediaQuery({ query: '(max-width: 960px)' });

	const [showRoomRateDetails, setShowRoomRateDetails] = useState(false);
	const [showFloorplanModal, setShowFloorPlanModal] = useState(false);
	const [showRoomRateModal, setShowRoomRateModal] = useState(false);

	return (
		<>
			<div className="flex flex-col w-full">
				<h4 className="spacing-heading-xs text-overline">
					<span className="text-text-primary">{itemTitle ?? t('Stay')}</span>
				</h4>
				<div className="flex flex-col spacing-stack-xs w-full">
					<div className="flex flex-row spacing-inline-s justify-start items-start w-full">
						<div className="flex flex-col spacing-stack-2xs flex-grow flex-2">
							<Button
								intent="unstyled"
								className="flex flex-row text-left flex-wrap text-subtitle2 cursor-pointer underline"
								onClick={() => setShowFloorPlanModal(true)}
							>
								<span className="text-text-primary underline">{name}</span>
							</Button>

							{cartDescription ? (
								<Button
									onClick={() => setShowRoomRateModal(true)}
									intent="unstyled"
									className="flex flex-row text-left flex-wrap cursor-pointer underline text-caption"
								>
									<span className="text-text-secondary">
										<BevSafeText>{cartDescription}</BevSafeText>
									</span>
								</Button>
							) : null}

							{(numberOfPeople || duration) && (
								<p className="text-caption">
									<span className="text-text-secondary">
										{[duration, numberOfPeople].filter(Boolean).join(' / ')}
									</span>
								</p>
							)}

							{ppMode && (
								<>
									{ppRate ? (
										<div className="flex flex-row items-start justify-start spacing-inline-xs">
											<div>
												<CheckmarkIcon height={18} width={18} />
											</div>
											<p className="text-caption">
												<span className="text-green-500 text-text-secondary">
													{t('Preferred Partner Benefits Included')}
												</span>
											</p>
										</div>
									) : (
										<div className="flex flex-row items-start justify-start spacing-inline-xs">
											<div>
												<CloseCircle fill="#EA0A0A" width={14} height={13.5} />
											</div>
											<p className="text-caption">
												<span className="text-red-500 text-text-secondary">
													{t('Preferred Partner Benefits NOT Included')}
												</span>
											</p>
										</div>
									)}
								</>
							)}

							{(quantity ?? 0) > 0 && (
								<p className="text-caption">
									<span className="text-text-secondary">
										{t('Quantity')}: {quantity}
									</span>
								</p>
							)}

							{requestDate && (
								<p className="text-caption">
									<span className="text-text-secondary">{requestDate}</span>
								</p>
							)}

							{removeItem && (
								<Button
									onClick={removeItem}
									intent="unstyled"
									className="flex flex-row items-center justify-center text-caption2 underline"
								>
									<span className="text-text-secondary">{t('Remove')}</span>
								</Button>
							)}
						</div>
						<div className="flex flex-col spacing-stack-2xs items-end justify-end flex-grow flex-1">
							{complimentaryPricingInfo ? (
								<p className="text-caption">
									<span className={isMobile ? 'text-caption2' : 'text-caption'}>
										{' '}
										{complimentaryPricingInfo}
									</span>
								</p>
							) : (
								<>
									{showInfoModal && unitType !== 'Price per unit' && (
										<Button
											title={t('Information')}
											onClick={showInfoModal}
											intent="unstyled"
											className="flex flex-row items-center justify-center text-caption2"
											data-cy="show-info-modal-btn"
										>
											<Information />
										</Button>
									)}
									<p className="text-subtitle2">
										<span className="text-text-primary">
											{handlePriceFormat({
												amount: price ?? 0,
												currencyCode,
											})}
										</span>
									</p>
								</>
							)}
							<p className="text-caption text-right" data-cy="shopping-cart-item__taxes-and-fees">
								<span className="text-text-secondary">{t(taxAndFees)}</span>
							</p>
						</div>
					</div>
					{roomRate ? (
						<div
							className={cn('flex flex-col items-end justify-center', {
								'spacing-stack-m': !isMobile,
								'spacing-stack-xs': isMobile,
							})}
						>
							<Button
								type="button"
								intent="unstyled"
								className="flex flex-row items-center justify-center gap-[10px] text-button2"
								onClick={() => {
									setShowRoomRateDetails(prev => !prev);
								}}
							>
								{showRoomRateDetails ? t('Hide Rate Details') : t('View Rate Details')}
								<div
									className={cn({
										'rotate-180': !showRoomRateDetails,
									})}
								>
									<IconCollapse fill="black" />
								</div>
							</Button>
							{showRoomRateDetails && (
								<ShoppingCartItemRateDetails
									roomRate={roomRate}
									currencyCode={currencyCode}
									stayResortFees={stayResortFees}
									taxAndFees={taxAndFees}
								/>
							)}
						</div>
					) : null}
				</div>
			</div>
			{showRoomRateModal && offer && (
				<RateCardDialog
					offerData={offer}
					isMultiRoom={isMultiRoom}
					isPreferredPartner={ppMode && ppRate}
					locale="en"
					closeModal={() => setShowRoomRateModal(false)}
				/>
			)}
			{showFloorplanModal && room && (
				<FloorPlanComponent room={room} closeModal={() => setShowFloorPlanModal(false)} />
			)}
		</>
	);
}
